import type { Plan } from '@parcel/types';
import type { SpecAnalyticsEventProperties } from './spec-events.js';

export type AppSurface = 'web' | 'marketing' | 'docs' | 'api' | 'mcp';
export type Environment = 'production' | 'development';

/** How a server event names its actor, so revenue attribution is auditable. */
export type ActorSource = 'user' | 'api_key' | 'workspace_fallback';

export type AuthMethod = 'microsoft' | 'google' | 'email_otp';
export type AuthMode = 'signin' | 'signup';
/** The @parcel/filters registry entities; every filter and Ask event names one. */
export type EntityKind = 'project' | 'account' | 'contact' | 'signal' | 'skill';

/** Distinguishes browsing into a record from an alert driving the user there. */
export type OpenSource =
  | 'table'
  | 'map'
  | 'search'
  | 'signal'
  | 'deep_link'
  | 'drawer_nav'
  | 'command_k'
  | 'spec'
  | 'feed';

/**
 * Only gates with a real paywall surface. Teams and Slack alerts are entitled
 * on Pro but render as a disabled row with a Lock badge
 * (`signal-subscriptions-settings.tsx:141-160`), never a LockedFeaturePanel and
 * never an UpgradeButton, so there is nothing to fire paywall_viewed or
 * upgrade_clicked from. Same reason data_feedback was dropped. Add the value
 * when the row becomes a real paywall, not before.
 */
export type PaywallGate = 'signal_history' | 'email_alerts' | 'api_keys' | 'seat_limit';

export type WelcomeStep = 'profile' | 'invite' | 'signals' | 'scoring' | 'finish';

/**
 * Re-exported, not redefined. These are the billing plans, and a local copy
 * would let the taxonomy drift into shipping a plan name the product does not
 * sell. This is also the only reason `@parcel/types` is a dependency; without
 * it the entry in package.json is dead weight.
 */
export type { Plan } from '@parcel/types';

/** Local: `@parcel/types` has no role union, and workspace roles are not billing. */
export type WorkspaceRole = 'owner' | 'admin' | 'member';

export type ProjectMapResultSizeBucket =
  | 'unknown'
  | '0'
  | '1-100'
  | '101-2000'
  | '2001-10000'
  | '10001+';
export type ProjectMapFeatureCountBucket =
  | 'unknown'
  | '0'
  | '1-10'
  | '11-100'
  | '101-1000'
  | '1001+';

/** Browseable CRM entities for infinite-table / cursor-list telemetry. */
export type EntityBrowseEntity = 'project' | 'account' | 'contact';
export type EntityBrowseMode = 'cursor' | 'offset';
export type EntityBrowseDirection = 'asc' | 'desc';

/** Rows returned by one list page (cursor pages cap at 100). */
export type EntityBrowsePageSizeBucket = 'unknown' | '0' | '1-99' | '100';

/** 1-based page index when known; `unknown` for later cursor pages on the server. */
export type EntityBrowsePageNumberBucket = 'unknown' | '1' | '2-5' | '6-20' | '21+';

/** Virtualized mounted row count (viewport + overscan), not loaded total. */
export type EntityBrowseMountedRowBucket = 'unknown' | '0' | '1-20' | '21-50' | '51-100' | '101+';

export function projectMapResultSizeBucket(count: number | null): ProjectMapResultSizeBucket {
  if (count === null || !Number.isFinite(count) || count < 0) return 'unknown';
  if (count === 0) return '0';
  if (count <= 100) return '1-100';
  if (count <= 2_000) return '101-2000';
  if (count <= 10_000) return '2001-10000';
  return '10001+';
}

export function projectMapFeatureCountBucket(count: number | null): ProjectMapFeatureCountBucket {
  if (count === null || !Number.isFinite(count) || count < 0) return 'unknown';
  if (count === 0) return '0';
  if (count <= 10) return '1-10';
  if (count <= 100) return '11-100';
  if (count <= 1_000) return '101-1000';
  return '1001+';
}

export function entityBrowsePageSizeBucket(count: number | null): EntityBrowsePageSizeBucket {
  if (count === null || !Number.isFinite(count) || count < 0) return 'unknown';
  if (count === 0) return '0';
  if (count < 100) return '1-99';
  return '100';
}

export function entityBrowsePageNumberBucket(
  pageNumber: number | null,
): EntityBrowsePageNumberBucket {
  if (pageNumber === null || !Number.isFinite(pageNumber) || pageNumber < 1) return 'unknown';
  if (pageNumber === 1) return '1';
  if (pageNumber <= 5) return '2-5';
  if (pageNumber <= 20) return '6-20';
  return '21+';
}

export function entityBrowseMountedRowBucket(count: number | null): EntityBrowseMountedRowBucket {
  if (count === null || !Number.isFinite(count) || count < 0) return 'unknown';
  if (count === 0) return '0';
  if (count <= 20) return '1-20';
  if (count <= 50) return '21-50';
  if (count <= 100) return '51-100';
  return '101+';
}

/**
 * Supabase calls the Microsoft provider 'azure'. The product, the dashboard and
 * this taxonomy all call it 'microsoft'. Mapping here rather than at each call
 * site means a new provider cannot be added without deciding its analytics name.
 */
export const SUPABASE_PROVIDER_TO_METHOD = {
  azure: 'microsoft',
  google: 'google',
} as const satisfies Record<string, AuthMethod>;

export type SupabaseOAuthProvider = keyof typeof SUPABASE_PROVIDER_TO_METHOD;

export function authMethodFromProvider(provider: SupabaseOAuthProvider): AuthMethod {
  return SUPABASE_PROVIDER_TO_METHOD[provider];
}

/**
 * The tolerant variant, for `session.user.app_metadata.provider`, which is an
 * untyped string and can be 'email' for the OTP path as well as 'azure' or
 * 'google'. Person properties must go through this or the person record says
 * 'azure' while the events say 'microsoft', which is the split the strict
 * mapping above exists to prevent.
 */
export function authMethodFromSupabaseProvider(provider: string | undefined): AuthMethod | null {
  if (provider === 'azure') return 'microsoft';
  if (provider === 'google') return 'google';
  if (provider === 'email') return 'email_otp';
  return null;
}

/**
 * Every event and its payload. Adding a key here is the only way to add an event;
 * `capture` is generic over this map, so a typo or a wrong property fails typecheck.
 */
export interface AnalyticsEvents {
  // Acquisition
  marketing_cta_clicked: { cta_id: string; destination: string };

  // Signup. ref: the dossier CTA that sent the visitor, dossier:{template}:{slug}; absent otherwise.
  signup_started: { method: AuthMethod; mode: AuthMode; ref?: string };
  signup_otp_sent: { mode: AuthMode };
  signup_completed: { method: AuthMethod; mode: AuthMode; ref?: string };
  signup_failed: { method: AuthMethod; reason: string };

  // Onboarding
  workspace_created: { workspace_id: string; slug: string; allowed_domains_count: number };
  welcome_step_completed: { step: WelcomeStep; step_index: number };
  welcome_step_skipped: { step: WelcomeStep; step_index: number };
  welcome_completed: { steps_skipped_count: number };
  // Fired server-side from the accept route, which has invite.workspaceId in hand.
  // An invite cannot confer owner, so the role union is narrower than WorkspaceRole.
  invite_accepted: { workspace_id: string; role: 'admin' | 'member' };

  // Activation candidates
  project_opened: { project_id: string; source: OpenSource };
  account_opened: { account_id: string; source: OpenSource };
  contact_opened: { contact_id: string; source: OpenSource };
  evidence_expanded: { entity_type: EntityKind; entity_id: string; project_id: string | null };
  record_expanded: {
    record_type: 'account' | 'contact' | 'project';
    record_id: string;
    source: 'button' | 'link' | 'default';
  };
  signal_opened: { signal_id: string; signal_type: string };
  feed_row_opened: { dataset: 'account' | 'project' };
  feed_row_seen: {
    dataset: 'account' | 'project';
    seen: boolean;
    how: 'expand' | 'button' | 'key';
  };
  filter_applied: { entity: EntityKind; field: string; operator: string; filter_count: number };
  ask_submitted: {
    entity: EntityKind;
    query: string;
    query_length: number;
    produced_filters: number;
  };
  saved_view_created: { entity: EntityKind };
  record_followed: {
    record_type: 'account' | 'project';
    followed: boolean;
    count: number;
    // dossier: applied on landing from a public page's follow=1 link, not a click.
    source: 'drawer' | 'toolbar' | 'dossier';
  };
  map_marker_clicked: { project_id: string };

  // Project map operations. These payloads deliberately contain no record,
  // View, URL, query, or raw-filter values.
  project_map_summary_response: {
    latency_ms: number;
    status: 200 | 400 | 403 | 404 | 500;
    result_size_bucket: ProjectMapResultSizeBucket;
  };
  project_map_tile_response: {
    db_latency_ms: number | null;
    response_bytes: number;
    cache_status: 'hit' | 'miss';
    z: number;
    feature_count_bucket: ProjectMapFeatureCountBucket;
  };
  project_map_first_idle: { correct_bounds_ms: number; first_idle_ms: number };
  project_map_partial_tile_error: { z: number };
  project_map_tile_retry: { z: number };

  // Entity browse (infinite table) operations. Payloads deliberately contain no
  // record values, names, emails, phones, search text, raw filters, cursor
  // contents, or View names.
  entity_browse_page_response: {
    entity: EntityBrowseEntity;
    mode: EntityBrowseMode;
    sort_id: string | null;
    direction: EntityBrowseDirection | null;
    result_size_bucket: EntityBrowsePageSizeBucket;
    latency_ms: number;
    db_latency_ms: number | null;
    page_number_bucket: EntityBrowsePageNumberBucket;
    response_bytes: number;
    status: 200 | 400 | 403 | 404 | 500;
  };
  entity_browse_first_rows: {
    entity: EntityBrowseEntity;
    latency_ms: number;
  };
  entity_browse_later_page_failure: {
    entity: EntityBrowseEntity;
    page_number_bucket: EntityBrowsePageNumberBucket;
  };
  entity_browse_later_page_retry: {
    entity: EntityBrowseEntity;
    page_number_bucket: EntityBrowsePageNumberBucket;
  };
  entity_browse_mounted_rows: {
    entity: EntityBrowseEntity;
    mounted_row_count_bucket: EntityBrowseMountedRowBucket;
  };

  // Habit
  signal_subscription_saved: { priority: 'high' | 'medium' | 'low' };
  member_invited: { count: number; role: WorkspaceRole };
  api_key_created: Record<string, never>;

  // Monetization
  paywall_viewed: { gate: PaywallGate; required_plan: Plan; current_plan: Plan };
  upgrade_clicked: { gate: PaywallGate; current_plan: Plan };
  checkout_started: { target_plan: Plan; seats: number };
  checkout_completed: { plan: Plan; seats: number; mrr: number; actor_source: ActorSource };
  subscription_changed: {
    from_plan: Plan | null;
    to_plan: Plan;
    seats: number;
    actor_source: ActorSource;
  };
  subscription_canceled: { plan: Plan; actor_source: ActorSource };

  // API, MCP, and the in-app Spec surface
  api_tool_called: {
    tool: string;
    entity: string;
    principal_type: 'user' | 'api_key';
    result_count: number;
    credits: number;
    // `spec` is the in-app copilot calling the same routes. It is metered by
    // the Spec credit ledger rather than usage.api_usage_events, so it writes
    // no meter row; this label is what keeps those calls visible without
    // letting them be counted as REST or MCP traffic. Unlike the other two,
    // it is proven by a shared secret, not asserted by a header.
    surface: 'rest' | 'mcp' | 'spec';
    actor_source: ActorSource;
  };

  spec_run_outcome: SpecAnalyticsEventProperties['spec_run_outcome'];
  spec_tool_outcome: SpecAnalyticsEventProperties['spec_tool_outcome'];
  spec_skill_outcome: SpecAnalyticsEventProperties['spec_skill_outcome'];
  spec_credit_outcome: SpecAnalyticsEventProperties['spec_credit_outcome'];
  spec_operations_health: SpecAnalyticsEventProperties['spec_operations_health'];
}

export type AnalyticsEventName = keyof AnalyticsEvents;

/** Registered on every event so any insight can be filtered by workspace. */
export interface SuperProperties {
  workspace_id: string | null;
  workspace_slug: string | null;
  plan: Plan | null;
  workspace_role: WorkspaceRole | null;
  environment: Environment;
  app: AppSurface;
}
